/**
 * ConsegnePage — «Da consegnare»: le aziende che hanno chiesto uno strumento che funziona solo dopo un nostro lavoro
 * (atlante cap. 264). Oggi è il centralino: l'amministratore ha premuto «Attiva», e a noi restano la pratica del
 * gestore coi documenti dell'azienda e la richiesta del numero. Il canone non parte finché non consegniamo.
 *
 * Risponde a una domanda: «a chi dobbiamo ancora un numero, da quanto, e conviene comprarlo?». In cima chi aspetta da
 * più tempo. Accanto a ogni riga, cosa succederebbe consegnando oggi: se l'azienda non potrebbe pagarlo (nessun
 * abbonamento né prova) lo dice, così non si compra un numero per un centralino che resterebbe spento.
 * La riga porta alla scheda «Strumenti» dell'azienda; «Assegna il numero» apre la stessa finestra di quella scheda.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { tokens } from '../theme';
import { SoftChip } from '../components/SoftChip';
import { data, ultimoGiorno } from '../components/strumenti/testiStrumenti';
import { DialogoAssegnaNumero } from '../components/telefono/DialogoAssegnaNumero';
import { comeHaChiesto, fraseAllaConsegna } from '../components/telefono/testiConsegna';
import { STRUMENTO_TELEFONO, leggiErrore, leggiRichiesteInAttesa, type RichiestaInAttesa } from '../services/consegneService';

const giorno = (iso: string) => ultimoGiorno(Date.parse(iso));

/** Da quanti giorni aspetta: è il numero che dice cosa fare per primo. */
function giorniDiAttesa(iso: string | null, oraMs: number): number | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? null : Math.max(0, Math.floor((oraMs - ms) / 86_400_000));
}

export const ConsegnePage: React.FC = () => {
  const navigate = useNavigate();
  const [richieste, setRichieste] = useState<RichiestaInAttesa[] | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [aperta, setAperta] = useState<RichiestaInAttesa | null>(null);
  const ora = Date.now();

  const leggi = useCallback(async () => {
    setErrore(null);
    try {
      setRichieste(await leggiRichiesteInAttesa());
    } catch (e) {
      setErrore(leggiErrore(e).messaggio);
      setRichieste([]);
    }
  }, []);

  useEffect(() => { void leggi(); }, [leggi]);

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontFamily: tokens.font.display, fontWeight: 600, fontSize: { xs: '1.6rem', md: '1.95rem' }, lineHeight: 1.1, letterSpacing: '-0.02em', color: tokens.color.graphite, fontVariationSettings: '"WONK" 0, "SOFT" 30' }}>
          Da consegnare
        </Typography>
        <Typography variant="body2" sx={{ color: tokens.color.ash, mt: 0.5, maxWidth: 640 }}>
          Le aziende che hanno chiesto il centralino e aspettano il numero. Non pagano niente finché il numero non
          risponde: quando Twilio lo consegna, «Assegna il numero» fa il resto e il canone parte quel giorno.
        </Typography>
      </Box>

      {errore && <Alert severity="error" sx={{ mb: 2 }}>{errore}</Alert>}

      <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
        {richieste === null ? (
          <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={24} /></Box>
        ) : richieste.length === 0 ? (
          <Typography sx={{ p: 4, color: tokens.color.ash }}>Nessuna azienda aspetta una consegna.</Typography>
        ) : (
          <Table size="small" sx={{ minWidth: 820 }}>
            <TableHead>
              <TableRow>
                <TableCell>Azienda</TableCell>
                <TableCell>Chiesto</TableCell>
                <TableCell>Il giorno della consegna</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {richieste.map((r) => {
                const attesa = giorniDiAttesa(r.richiesta_il, ora);
                const consegna = fraseAllaConsegna(r.alla_consegna, giorno);
                const telefono = r.strumento === STRUMENTO_TELEFONO;
                return (
                  <TableRow
                    key={`${r.tenant_id}-${r.strumento}`}
                    hover
                    onClick={() => navigate(`/tenants/${r.tenant_id}?tab=strumenti`)}
                    sx={{ cursor: 'pointer', verticalAlign: 'top' }}
                  >
                    <TableCell>
                      <Typography sx={{ fontWeight: 600 }}>{r.nome_azienda}</Typography>
                      <Typography variant="caption" sx={{ color: tokens.color.ash }}>{r.etichetta}</Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                        <Typography variant="body2">{r.richiesta_il ? data(Date.parse(r.richiesta_il)) : '—'}</Typography>
                        {attesa !== null && (
                          <SoftChip label={attesa === 0 ? 'oggi' : attesa === 1 ? 'da 1 giorno' : `da ${attesa} giorni`} tone={attesa >= 7 ? 'warning' : 'default'} />
                        )}
                      </Stack>
                      <Typography variant="caption" sx={{ color: tokens.color.ash }}>
                        {r.richiesta_da_nome ?? 'persona non nota'} · {comeHaChiesto(r.richiesta_via)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 360 }}>
                      <Typography variant="body2" sx={{ color: consegna.attenzione ? tokens.color.warning : tokens.color.graphite }}>
                        {consegna.testo}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {telefono && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={(e) => { e.stopPropagation(); setAperta(r); }}
                          sx={{ textTransform: 'none', minHeight: 36, whiteSpace: 'nowrap' }}
                        >
                          Assegna il numero
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Paper>

      {aperta && (
        <DialogoAssegnaNumero
          aperto
          onChiudi={() => setAperta(null)}
          tenantId={aperta.tenant_id}
          nomeAzienda={aperta.nome_azienda}
          allaConsegna={aperta.alla_consegna}
          onFatto={() => { void leggi(); }}
        />
      )}
    </Box>
  );
};
